import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ColumnDefinition, ConfigurationTable } from './ConfigurationTable';
import { useAtom, useAtomValue } from 'jotai';
import {
  allPOITypes,
  riskConfigData,
  selectedTemplateId,
} from '@/components/TemplateBuilder/state';
import { stripTypenameFromRemediationConfig } from '../utils';
import {
  GetAllPointOfInterestTemplatesQuery,
  PointOfInterestTemplateField,
  useAllPointOfInterestTemplateLazyQuery,
  RemediationScorePoiField,
  RemediationScorePoiSectionInput,
  useUpdateRemediationScoreConfigMutation,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { Field } from '../TemplateForm/styles';
import { Box, MenuItem, TextField, IconButton } from '@mui/material';
import { Save, Delete } from '@mui/icons-material';

type PoisConfigItem = {
  id: string;
  fields: string;
  score: number;
};

export const PoisConfiguration = ({
  structureId,
  templateId,
}: {
  structureId: string;
  templateId: string;
}) => {
  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const [poisItems, setPoisItems] = useState<PoisConfigItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<PointOfInterestTemplateField | null>(
    null
  );
  const [poiScore, setPoiScore] = useState<number>(0);
  const [originalPoiScore, setOriginalPoiScore] = useState<number>(0);
  const [hasPoiScoreChanged, setHasPoiScoreChanged] = useState<boolean>(false);

  const [selectedPOI, setSelectedPOI] = useState<
    GetAllPointOfInterestTemplatesQuery['allPointOfInterestTemplate'][number] | null
  >(null);

  const [allPOIsTypes, setAllPOIsTypes] = useAtom(allPOITypes);
  const [riskConfig, setRiskConfig] = useAtom(riskConfigData);
  const [getPoiTemplates, { data, loading }] = useAllPointOfInterestTemplateLazyQuery({
    variables: {
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId || '',
    },
    fetchPolicy: 'cache-first',
  });

  const [updateRemediationScoreConfig] = useUpdateRemediationScoreConfigMutation();

  useEffect(() => {
    if (!allPOIsTypes) {
      getPoiTemplates();
    }
  }, [allPOIsTypes, getPoiTemplates]);

  useEffect(() => {
    if (data?.allPointOfInterestTemplate && !allPOIsTypes) {
      setAllPOIsTypes(data.allPointOfInterestTemplate);
    }
  }, [data?.allPointOfInterestTemplate, allPOIsTypes, setAllPOIsTypes]);

  useEffect(() => {
    if (
      riskConfig &&
      riskConfig.poi &&
      riskConfig.poi.fields &&
      riskConfig.poi?.fields?.length > 0
    ) {
      const riskId = riskConfig.poi.fields.map((field) => field.poiTemplateKey);
      const selectedId = riskId.find((id) => id === selectedPOI?.key);
      if (selectedId) {
        const riskField = riskConfig.poi.fields.find(
          (field) => field.poiTemplateKey === selectedId
        );
        if (riskField) {
          const scoreValue = riskField?.poiScore || 0;
          setPoiScore(scoreValue);
          setOriginalPoiScore(scoreValue);
          setHasPoiScoreChanged(false);

          setSelectedComponent(
            selectedPOI?.fields?.find((field) => field.key === riskField.subcategory?.fieldKey) ||
              null
          );
          if (riskField.subcategory && riskField.subcategory && riskField.subcategory.fieldKey) {
            if (riskField.subcategory.optionScores) {
              const convertedItems: PoisConfigItem[] = Object.entries(
                riskField.subcategory.optionScores
              ).map(([key, value], index) => ({
                id: `${Date.now()}-${index}`,
                fields: key,
                score: value as number,
              }));
              setPoisItems(convertedItems);
            }
          }
        }
      }
    }
  }, [riskConfig, riskConfig?.poi?.fields, selectedPOI?.fields, selectedPOI?.id, selectedPOI?.key]);

  const columns = useMemo(
    (): ColumnDefinition<PoisConfigItem>[] => [
      {
        key: 'fields',
        label: 'Fields',
        width: 3,
        type: 'select',
        options:
          selectedComponent?.options?.map((option) => ({
            value: option,
            label: option,
          })) || [],
      },
      {
        key: 'score',
        label: 'Score',
        width: 3,
        type: 'number',
        inputProps: { step: 0.01, min: 1 },
      },
    ],
    [selectedComponent?.options]
  );

  const handleEditStart = (item: PoisConfigItem) => {
    setEditingId(item.id);
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };

  const handleEditSave = (
    id: string,
    field: keyof Omit<PoisConfigItem, 'id'>,
    value: string | number
  ) => {
    const updatedItems = poisItems.map((item) =>
      item.id === id
        ? { ...item, [field]: field === 'score' ? parseFloat(value as string) : value }
        : item
    );
    setPoisItems(updatedItems);
    setEditingId(null);
    savePoisConfiguration(updatedItems);
  };

  const handleInlineChange = (
    id: string,
    field: keyof Omit<PoisConfigItem, 'id'>,
    value: string | number
  ) => {
    setPoisItems((prev) => {
      const updated = prev.map((item) =>
        item.id === id
          ? { ...item, [field]: field === 'score' ? parseFloat(value as string) : value }
          : item
      );

      return updated;
    });
  };

  const handlePoiScoreChange = (value: number) => {
    setPoiScore(value);
    setHasPoiScoreChanged(value !== originalPoiScore);
  };

  const handleSavePoiScore = () => {
    setOriginalPoiScore(poiScore);
    setHasPoiScoreChanged(false);
    savePoisConfiguration();
  };

  const handleAddNew = (newItem: Omit<PoisConfigItem, 'id'>) => {
    const itemWithId: PoisConfigItem = {
      ...newItem,
      id: Date.now().toString(),
      score: typeof newItem.score === 'string' ? parseFloat(newItem.score) : newItem.score,
    };
    const updatedItems = [...poisItems, itemWithId];
    setPoisItems(updatedItems);
    savePoisConfiguration(updatedItems);
  };

  const handleDelete = (item: PoisConfigItem) => {
    const updatedItems = poisItems.filter((poisItem) => poisItem.id !== item.id);
    setPoisItems(updatedItems);
    savePoisConfiguration(updatedItems);
  };

  const handleDeleteSubcategory = async () => {
    if (!selectedPOI || !selectedComponent) return;

    // Clear the table data
    setPoisItems([]);

    // Reset the selected component
    setSelectedComponent(null);

    // Remove the subcategory from the risk configuration
    const existingFields = riskConfig.poi?.fields || [];
    const existingFieldIndex = existingFields.findIndex(
      (field) => field.poiTemplateKey === selectedPOI.key
    );

    if (existingFieldIndex !== -1) {
      const updatedFields = [...existingFields];
      // Remove subcategory but keep the POI field with just the poiScore
      updatedFields[existingFieldIndex] = {
        poiTemplateKey: selectedPOI.key || '',
        poiScore: poiScore,
      };

      const poiSection: RemediationScorePoiSectionInput = {
        fields: updatedFields,
      };

      try {
        await updateRemediationScoreConfig({
          variables: {
            input: {
              structureId,
              templateId,
              remediationScoreConfig: {
                area: riskConfig.area,
                poi: poiSection,
                cof: riskConfig.cof,
              },
            },
          },
        }).then((response) => {
          if (response.data?.updateRemediationScoreConfig?.remediationScoreConfig) {
            const cleanConfig = stripTypenameFromRemediationConfig(
              response.data.updateRemediationScoreConfig.remediationScoreConfig
            );
            setRiskConfig(cleanConfig);
          }
        });
      } catch (error) {
        console.error('Failed to delete subcategory configuration:', error);
      }
    }
  };

  // Shared function to save POI configuration data
  const savePoisConfiguration = async (itemsToSave?: PoisConfigItem[]) => {
    if (!selectedPOI || !selectedPOI.key) return;
    const items = itemsToSave || poisItems;

    const poiData: RemediationScorePoiField = {
      poiTemplateKey: selectedPOI.key,
      poiScore: poiScore,
    };

    // Add subcategory if component is selected and has option scores
    if (selectedComponent && items.length > 0) {
      const optionScores: Record<string, number> = {};
      items.forEach((item) => {
        optionScores[item.fields] = item.score;
      });

      poiData.subcategory = {
        fieldKey: selectedComponent.key || selectedComponent.id,
        optionScores: optionScores,
      };
    }

    // replace if exists, add if new
    const existingFields = riskConfig.poi?.fields || [];
    const existingFieldIndex = existingFields.findIndex(
      (field) => field.poiTemplateKey === poiData.poiTemplateKey
    );

    let updatedFields;
    if (existingFieldIndex !== -1) {
      updatedFields = [...existingFields];
      updatedFields[existingFieldIndex] = poiData;
    } else {
      updatedFields = [...existingFields, poiData];
    }

    const poiSection: RemediationScorePoiSectionInput = {
      fields: updatedFields,
    };

    try {
      await updateRemediationScoreConfig({
        variables: {
          input: {
            structureId,
            templateId,
            remediationScoreConfig: {
              area: riskConfig.area,
              poi: poiSection,
              cof: riskConfig.cof,
            },
          },
        },
      }).then((response) => {
        if (response.data?.updateRemediationScoreConfig?.remediationScoreConfig) {
          const cleanConfig = stripTypenameFromRemediationConfig(
            response.data.updateRemediationScoreConfig.remediationScoreConfig
          );
          setRiskConfig(cleanConfig);
        }
      });
    } catch (error) {
      console.error('Failed to save POI configuration:', error);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">POIs Configuration</Typography>
      </Stack>

      {loading && <PageLoader />}
      {!loading && (
        <Stack direction="row" gap={2}>
          <Stack direction="column">
            {allPOIsTypes?.map((poi) => (
              <Field
                selected={selectedPOI?.id === poi.id}
                key={poi?.id}
                onClick={() => {
                  setSelectedPOI(poi);
                  setSelectedComponent(null); // Reset component selection
                  setPoisItems([]); // Reset table data
                  setPoiScore(0); // Reset POI score
                  setOriginalPoiScore(0); // Reset original POI score
                  setHasPoiScoreChanged(false); // Reset change tracking
                }}
              >
                {poi.name}
              </Field>
            ))}
          </Stack>

          {selectedPOI && (
            <Box width="100%">
              <Stack direction="row" gap={2} alignItems="center" pb={2}>
                <Typography variant="body2">Select score for {selectedPOI.name}</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={poiScore}
                  onChange={(e) => handlePoiScoreChange(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                {hasPoiScoreChanged && (
                  <IconButton
                    size="small"
                    onClick={handleSavePoiScore}
                    sx={{ color: 'success.main' }}
                    title="Save POI Score"
                  >
                    <Save fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Stack direction="row" gap={2} alignItems="center" pb={2}>
                <Typography variant="body2">Select the sub category (optional)</Typography>
                <TextField
                  select
                  size="small"
                  sx={{ width: '20%' }}
                  value={selectedComponent?.key || ''}
                  onChange={(e) => {
                    const selectedField = selectedPOI?.fields.find(
                      (field) => field.key === e.target.value
                    );
                    setSelectedComponent(selectedField || null);
                    setPoisItems([]); // Reset table data when component changes
                  }}
                >
                  {selectedPOI?.fields.map((field) => {
                    if (field.options.length > 0 && field.key) {
                      return (
                        <MenuItem key={field.id} value={field.key}>
                          {field.name}
                        </MenuItem>
                      );
                    }
                  })}
                </TextField>
                {selectedComponent && (
                  <IconButton
                    size="small"
                    onClick={handleDeleteSubcategory}
                    sx={{ color: 'error.main' }}
                    title="Delete subcategory and clear table data"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              {selectedComponent && (
                <ConfigurationTable
                  items={poisItems}
                  columns={columns}
                  editingId={editingId}
                  emptyMessage="No POI configurations added yet. Click 'Add New Row' to create one."
                  onEditStart={handleEditStart}
                  onEditCancel={handleEditCancel}
                  onEditSave={handleEditSave}
                  onInlineChange={handleInlineChange}
                  onAddNew={handleAddNew}
                  onDelete={handleDelete}
                />
              )}
            </Box>
          )}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" py={2}>
        <Typography variant="h6">POI Equation for asset: </Typography>
        <Typography variant="body2" ml={1}>
          Count By Type * Sub Category * Score
        </Typography>
      </Stack>
    </>
  );
};
